import { useEffect, useState, useMemo, useRef } from "react";
import { api, TASK_CLASS_LABELS, DAY_TYPE_COLORS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TodayBar from "@/components/TodayBar";
import SectionStats from "@/pages/SectionStats";
import FileAnalysis from "@/components/FileAnalysis";
import Awards, { AwardsBadge } from "@/components/Awards";
import ReportsPanel from "@/components/ReportsPanel";
import BodyComposition from "@/components/BodyComposition";
import TrifitConnect from "@/components/TrifitConnect";
import { usePermissions } from "@/lib/perms";
import { Watch, ListChecks, ClipboardList, Trophy, Plug, RefreshCw, ChevronDown, ChevronRight, Send, TrendingUp } from "lucide-react";
import { TargetCurvePanel } from "@/components/TargetCurvePanel";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import Tip from "@/components/Tip";

const RANGES = [["today", "Today"], ["l7", "Last 7"], ["l30", "Last 30"], ["l90", "Last 90"], ["lifetime", "Lifetime"]];
const RANGE_DAYS = { l7: 7, l30: 30, l90: 90 };
const todayStr = () => new Date().toISOString().slice(0, 10);
function inRange(dateStr, range) {
  if (range === "lifetime") return true;
  if (!dateStr) return false;
  if (range === "today") return dateStr === todayStr();
  const diff = (new Date(todayStr() + "T00:00:00") - new Date(dateStr + "T00:00:00")) / 86400000;
  return diff >= 0 && diff < RANGE_DAYS[range];
}

const WEARABLE_ROWS = [
  ["rest", "Rest+"], ["effort", "Effort+"], ["readiness", "Readiness"], ["sharpness_wearable", "Sharpness (device)"],
  ["mind_score", "Mind Score"], ["body_score", "Body Score"], ["soul_score", "Soul Score"],
  ["hrv_avg", "HRV Avg"], ["hrv", "HRV"], ["hrv_min", "HRV Min"], ["hrv_max", "HRV Max"],
  ["hr_avg", "HR Avg"], ["hr_min", "HR Min"], ["hr_max", "HR Max"], ["resting_hr", "Resting HR"],
  ["stress_avg", "Stress Avg"], ["stress_min", "Stress Min"], ["stress_max", "Stress Max"],
  ["bp_sys_avg", "BP Sys Avg"], ["bp_sys_min", "BP Sys Min"], ["bp_sys_max", "BP Sys Max"],
  ["bp_dia_avg", "BP Dia Avg"], ["bp_dia_min", "BP Dia Min"], ["bp_dia_max", "BP Dia Max"],
  ["steps", "Steps"], ["distance_km", "Distance"], ["calories", "Calories"],
  ["sleep_hours", "Sleep"], ["sleep_deep_h", "Deep Sleep"], ["sleep_light_h", "Light Sleep"], ["sleep_rem_h", "REM Sleep"],
];
const LOWER_BETTER_WEAR = new Set(["hr_avg", "hr_min", "hr_max", "resting_hr", "stress_avg", "stress_min", "stress_max", "bp_sys_avg", "bp_sys_min", "bp_sys_max", "bp_dia_avg", "bp_dia_min", "bp_dia_max"]);
// zone -> [score label, key into ir, invert(lower better)]
const ZONE_SCORE = {
  drills: ["Sharpness", "sharpness", false],
  snc: ["Readiness", "readiness", false],
  nutrition: ["Effort", "effort", false],
  mind: ["Mind", "mind", false],
  physio: ["Injury Risk", "injury_risk", true],
  medical: ["Injury Risk", "injury_risk", true],
};
function ScoreChip({ zone, snap, ir, wear }) {
  const cfg = ZONE_SCORE[zone];
  if (!cfg) return null;
  let [label, key, invert] = cfg;
  let val = snap?.[key];
  if (key === "injury_risk") val = ir?.composite ?? snap?.injury_risk;
  if (key === "mind") val = wear?.mind_score?.latest;
  if (val == null) return null;
  const good = invert ? val < 34 : val >= 50;
  return (
    <span data-testid={`zone-score-${zone}`} className="ml-auto flex items-center gap-1.5 text-xs">
      <span className="tracking-[0.15em] uppercase font-bold text-muted-foreground">{label}</span>
      <span className="font-heading font-black tabular-nums">{Math.round(val)}</span>
      <span className={good ? "text-traffic-green" : "text-traffic-red"}>{good ? "▲" : "▼"}</span>
    </span>
  );
}
const NUTRI_PRESETS = ["protein_g", "carbs_g", "fats_g", "hydration_l", "supplement"];
// domain -> primary tag shown on each Performance zone
const ZONES = [
  ["drills", "Drill Stats", "Sharpness", []],
  ["snc", "S&C Stats", "Readiness", []],
  ["nutrition", "Nutrition Logs (incl. supplements)", "Soul", NUTRI_PRESETS],
  ["mind", "Mind Logs", "Mind", []],
  ["physio", "Physio Logs", "Injury Risk", []],

];


const SEC_OF = { drills: "performance", snc: "performance", mind: "performance", match: "matches",
  nutrition: "nutrition", physio: "physio", medical: "medical" };
const JUMP_SEC = { "sec-nutrition": "nutrition", "sec-physio": "physio", "sec-medical": "medical",
  "sec-match": "matches" };
const JUMP = [
  ["sec-awards", "Awards"],
  ["sec-targets", "Targets"],
  ["sec-analysis", "Files & notes"],
  ["sec-wearables", "Wearables"],
  ["sec-snc", "S&C"],
  ["sec-drills", "Skills"],
  ["sec-nutrition", "Nutrition"],
  ["sec-mind", "Mind"],
  ["sec-physio", "Physio"],
  ["sec-bodycomp", "Body Comp"],
  ["sec-timeline", "Timeline"],
  ["sec-reports", "Reports"],
];

// jump straight to a section instead of scrolling the whole page
function SectionNav() {
  const { can } = usePermissions();
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="border-b border-border scroll-x" data-testid="perf-section-nav">
      <div className="flex gap-1.5 px-4 sm:px-8 py-2.5 min-w-max">
        {JUMP.filter(([id]) => can(JUMP_SEC[id] || "performance")).map(([id, label]) => (
          <button key={id} data-testid={`jump-${id}`} onClick={() => go(id)}
            className="text-[11px] uppercase tracking-wider border border-border px-2.5 py-1.5 hover:border-foreground hover:bg-surface transition-colors whitespace-nowrap">
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid, silo, athleteId, notes, onAddNote, anchor }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const list = (notes && silo && notes[silo]) || [];
  return (
    <div id={anchor} className="border-b border-border p-8 scroll-mt-24" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex items-center gap-2 flex-1"><Icon className="w-4 h-4" /> {title}</p>
        {silo && athleteId && <button data-testid={`add-note-${silo}`} onClick={() => setOpen((o) => !o)} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">+ Note</button>}
      </div>
      {open && (
        <div className="flex gap-2 mb-4">
          <input data-testid={`note-input-${silo}`} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={`Note for ${silo}…`} className="inp text-sm flex-1" />
          <button data-testid={`note-save-${silo}`} onClick={async () => { if (txt.trim()) { await onAddNote(silo, txt); setTxt(""); setOpen(false); } }} className="text-sm px-3 py-1.5 bg-foreground text-background hover:bg-neutral-800 transition-colors">Save</button>
        </div>
      )}
      {list.length > 0 && (
        <div className="border border-border divide-y divide-border mb-4 bg-surface" data-testid={`notes-${silo}`}>
          {list.map((n) => <div key={n.note_id} className="p-2 text-sm"><span>{n.text}</span> <span className="text-xs text-muted-foreground">- {n.by}</span></div>)}
        </div>
      )}
      {children}
    </div>
  );
}

export default function Performance({ athleteId: propId, embedded }) {
  const { user } = useAuth();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const [athletes, setAthletes] = useState([]);
  const [athleteId, setAthleteId] = useState(propId || "");
  const [d, setD] = useState(null);
  const [nutri, setNutri] = useState([]);
  const [coach, setCoach] = useState([]);
  const [providers, setProviders] = useState([]);
  const [ow, setOw] = useState(null);
  const [notes, setNotes] = useState({});
  const [range, setRange] = useState("lifetime");
  const [wearNoteOpen, setWearNoteOpen] = useState(false);
  const [wearNote, setWearNote] = useState("");
  const [ir, setIr] = useState(null);
  const [snap, setSnap] = useState(null);
  const [tlAll, setTlAll] = useState(false);
  const [wearOpen, setWearOpen] = useState(true);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("wearables") !== "1") return;
    setWearOpen(true);
    const t = setTimeout(() => document.querySelector('[data-testid="wearables-panel"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" }), 900);
    return () => clearTimeout(t);
  }, []);
  const loadIr = () => athleteId && api.get(`/athletes/${athleteId}/injury-risk`).then((r) => setIr(r.data)).catch(() => setIr(null));
  useEffect(() => { setIr(null); loadIr(); /* eslint-disable-next-line */ }, [athleteId]);

  useEffect(() => { api.get("/athletes").then((r) => { setAthletes(r.data); if (!propId && r.data[0]) setAthleteId(r.data[0].athlete_id); }); }, [propId]);
  const reload = () => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/performance`).then((r) => setD(r.data));
    if (can("nutrition")) api.get(`/athletes/${athleteId}/nutrition`).then((r) => { setNutri(r.data.rows); setCoach(r.data.coach_summary || []); }).catch(() => { setNutri([]); setCoach([]); });
    api.get(`/wearables/openwearables/status?athlete_id=${athleteId}`).then((r) => setOw(r.data)).catch(() => setOw(null));
    api.get(`/athletes/${athleteId}/notes`).then((r) => setNotes(r.data.notes || {})).catch(() => setNotes({}));
    api.get(`/athletes/${athleteId}/snapshot`).then((r) => setSnap(r.data)).catch(() => setSnap(null));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (permissionsLoaded) reload(); }, [athleteId, permissionsLoaded]);
  const addNote = async (silo, text) => { await api.post(`/athletes/${athleteId}/notes`, { silo, text }); toast.success("Note added"); reload(); };
  const [syncing, setSyncing] = useState(false);
  const [pushingScores, setPushingScores] = useState(false);
  const [athMeta, setAthMeta] = useState(null);
  useEffect(() => { if (athleteId) api.get(`/athletes/${athleteId}`).then((r) => setAthMeta(r.data)).catch(() => setAthMeta(null)); }, [athleteId]);
  // Auto-refresh Trifit data on load (once per athlete per session) so Rest/Effort stay current.
  const autoSyncedRef = useRef(new Set());
  useEffect(() => {
    if (!athleteId || !athMeta?.trifit_email) return;
    if (autoSyncedRef.current.has(athleteId)) return;
    autoSyncedRef.current.add(athleteId);
    setSyncing(true);
    api.post(`/trifit/resync/${athleteId}`)
      .then((r) => { if (r.data?.ok) { reload(); loadIr(); window.dispatchEvent(new CustomEvent("trifit-synced", { detail: { athleteId } })); } })
      .catch(() => {})
      .finally(() => setSyncing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId, athMeta]);
  // Any sync (from here or another panel) refreshes injury risk + all Performance data together.
  useEffect(() => {
    if (!athleteId) return;
    const onSynced = () => { reload(); loadIr(); };
    window.addEventListener("trifit-synced", onSynced);
    return () => window.removeEventListener("trifit-synced", onSynced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);
  const syncTrifit = async () => {
    if (!athleteId) return;
    setSyncing(true);
    toast.info("Syncing latest wearable + activity data from Trifit...");
    try {
      const r = await api.post(`/trifit/resync/${athleteId}`);
      if (r.data.ok) { toast.success("Synced from Trifit"); reload(); loadIr(); window.dispatchEvent(new CustomEvent("trifit-synced", { detail: { athleteId } })); }
      else toast.error("Sync failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "No Trifit account linked"); }
    finally { setSyncing(false); }
  };
  const pushScores = async () => {
    if (!athleteId) return;
    setPushingScores(true);
    try {
      const r = await api.post(`/athletes/${athleteId}/push-scores`);
      const p = r.data.pushed?.pushed || {};
      toast.success(`Scores written to phone (${r.data.pushed?.date}): ${Object.keys(p).join(", ")}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Push failed"); }
    finally { setPushingScores(false); }
  };
  const toggleSharing = async (val) => {
    try {
      await api.post(`/athletes/${athleteId}/data-sharing`, { data_sharing: val });
      setAthMeta((m) => ({ ...m, data_sharing: val }));
      toast.success(val ? "Data sharing on · scores can sync to phone" : "Data sharing off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const fTasks = useMemo(() => (d?.timeline || []).filter((t) => inRange(t.date, range)), [d, range]);
  const fNutri = useMemo(() => nutri.filter((r) => inRange(r.date, range)), [nutri, range]);

  const [connecting, setConnecting] = useState(null);
  // auto-freshen wearable data whenever this athlete is opened (throttled server-side)
  useEffect(() => {
    if (!athleteId) return;
    api.post(`/athletes/${athleteId}/wearables/refresh`).then((r) => {
      if (r.data?.queued) setTimeout(() => { reload(); loadIr(); }, 7000);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [athleteId]);
  const connectOw = async (provider) => {
    setConnecting(provider);
    try {
      const redirect_url = `${window.location.origin}/wearables/connected?athlete_id=${athleteId}&provider=${provider}`;
      const r = await api.post(`/wearables/openwearables/connect`, { athlete_id: athleteId, provider, redirect_url });
      if (r.data.authorization_url) { window.location.assign(r.data.authorization_url); }
      else toast.message("Connection started · complete it on the provider screen");
    } catch (e) { toast.error(e?.response?.data?.detail || "Connect failed"); }
    finally { setConnecting(null); }
  };
  const disconnectOw = async (provider) => {
    try { await api.post(`/wearables/openwearables/disconnect`, { athlete_id: athleteId, provider }); toast.success(`${provider} disconnected`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const syncOw = async () => {
    toast.info("Pulling latest from connected wearables…");
    try {
      const r = await api.post(`/wearables/openwearables/sync/${athleteId}`);
      if (r.data.days_written > 0) {
        toast.success(`Synced ${r.data.days_written} day(s) from wearables`);
      } else {
        const diag = r.data.diagnostics || {};
        const rc = diag.raw_counts || {};
        toast.warning("Sync returned 0 day(s)", {
          description: (diag.last_error || "Provider returned no rows") +
            ` · pulled activity ${rc.activity ?? 0}, sleep ${rc.sleep ?? 0}, recovery ${rc.recovery ?? 0}, scores ${rc.scores ?? 0}`,
        });
      }
      reload(); loadIr(); window.dispatchEvent(new CustomEvent("trifit-synced", { detail: { athleteId } }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
  };
  const backfillOw = async () => {
    toast.info("Asking the provider for history · this takes a moment…");
    try {
      const r = await api.post(`/wearables/openwearables/backfill/${athleteId}?days=90`);
      if (r.data.days_written > 0) toast.success(`Backfilled ${r.data.days_written} day(s)`);
      else toast.warning("Backfill wrote 0 day(s)", { description: r.data.diagnostics?.last_error || r.data.note });
      reload(); loadIr(); window.dispatchEvent(new CustomEvent("trifit-synced", { detail: { athleteId } }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
  };
  const autoFix = async (date) => {
    const r = await api.post(`/athletes/${athleteId}/nutrition/auto-fix?date=${date}`);
    toast.success(r.data.created ? "Top-up added to that day" : "Day already on target");
    reload();
  };

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center px-4 sm:px-8 py-3 gap-4 sticky top-0 bg-background z-20">
        <h1 className="font-heading text-xl font-black tracking-tight">Training</h1>
        {!embedded && (
        <select data-testid="perf-athlete-select" value={athleteId} onChange={(e) => setAthleteId(e.target.value)} className="border border-border bg-background text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground">
          {athletes.map((a) => <option key={a.athlete_id} value={a.athlete_id}>{a.name}</option>)}
        </select>
        )}
        <button data-testid="trifit-sync-btn" onClick={syncTrifit} disabled={syncing || !athleteId} className="ml-auto flex items-center gap-1.5 text-xs border border-foreground px-3 py-1.5 font-bold uppercase tracking-wider hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing" : "Sync Trifit"}
        </button>
        {athMeta?.trifit_email && (
          <>
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer" data-testid="data-sharing-toggle" title="Athlete consent to write app scores back to their Trifit phone">
              <input type="checkbox" checked={!!athMeta?.data_sharing} onChange={(e) => toggleSharing(e.target.checked)} /> Shares data
            </label>
            <button data-testid="push-scores-btn" onClick={pushScores} disabled={pushingScores || !athMeta?.data_sharing} title={athMeta?.data_sharing ? "Write the app's ML-blended scores to the athlete's phone" : "Enable data sharing first"} className="flex items-center gap-1.5 text-xs border border-foreground px-3 py-1.5 font-bold uppercase tracking-wider hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {pushingScores ? "Pushing" : "Scores → phone"}
            </button>
          </>
        )}
      </header>
      {!embedded && <TodayBar athleteId={athleteId} />}

      {!d ? <div className="p-8 text-sm text-muted-foreground">Loading…</div> : (
        <>
          {athleteId && <SectionNav />}
          {athleteId && (
            <div id="sec-awards" className="border-b border-border p-4 sm:p-8 scroll-mt-24" data-testid="perf-awards">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Trophy className="w-4 h-4" />
                <h2 className="font-heading font-bold tracking-tight">Awards &amp; honours</h2>
                <AwardsBadge athleteId={athleteId} />
              </div>
              <Awards athleteId={athleteId} canEdit={user?.role !== "athlete"} />
            </div>
          )}
          {athleteId && user?.role !== "athlete" && (
            <div id="sec-targets" className="border-b border-border p-4 sm:p-8 scroll-mt-24" data-testid="perf-targets">
              <div className="flex items-center gap-3 mb-4"><TrendingUp className="w-4 h-4" /><h2 className="font-heading font-bold tracking-tight">Development targets</h2></div>
              <TargetCurvePanel athleteId={athleteId} />
            </div>
          )}
          {/* Injury risk moved to Command Center / Athlete Overview · one number, one place */}

          {/* Domain zones · each with add metric, log, trends */}
          <div id="sec-analysis" className="border-b border-border p-8 scroll-mt-24" data-testid="perf-entry">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Domains · log data & track trends</p>
            <p className="text-sm text-muted-foreground mb-6 max-w-2xl">Each domain is its own zone. Define metrics (name · unit · direction · target), log values or import a CSV, and watch progress over time.</p>

            <FileAnalysis athleteId={athleteId} />

            <div id="sec-wearables" className="border border-border mb-6 scroll-mt-24" data-testid="zone-wearables">
              <button onClick={() => setWearOpen((o) => !o)} className="w-full px-5 py-4 border-b border-border flex items-center gap-2 hover:bg-surface transition-colors" data-testid="wearables-collapse-toggle">
                {wearOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Watch className="w-4 h-4" /><h3 className="font-heading font-bold tracking-tight flex-1 text-left">Wearables</h3>
                <span className="text-xs text-muted-foreground">{wearOpen ? "Hide" : "Show"}</span>
              </button>
              {wearOpen && (
              <div className="p-5">
                <TrifitConnect athleteId={athleteId} defaultEmail={athMeta?.trifit_email || user?.email || ""}
                  onSynced={() => { reload(); loadIr(); }} />
                <div className="mb-4 scroll-mt-24" data-testid="wearables-panel">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Connect wearables</span>
                    <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">via OpenWearables</span>
                    {ow && !ow.configured && <span className="text-[10px] uppercase tracking-widest text-traffic-amber" data-testid="ow-not-configured">not connected</span>}
                    {ow?.configured && (
                      <div className="ml-auto flex items-center gap-2">
                      <button data-testid="ow-sync-btn" onClick={syncOw} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Sync</button>
                      {user?.role !== "athlete" && (
                        <button data-testid="ow-backfill-btn" onClick={backfillOw}
                          title="A normal sync only asks the provider for data since the last successful sync. Backfill asks for the last 90 days, which is what you need after a broken or re-authorised connection."
                          className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">Backfill 90 days</button>
                      )}
                      </div>
                    )}
                  </div>
                  {ow?.diagnostics?.last_error && ow?.last_sync && (
                    <p className="text-[11px] text-traffic-amber mb-2 border border-traffic-amber/40 px-2 py-1" data-testid="ow-diag">
                      Last sync note: {ow.diagnostics.last_error}
                    </p>
                  )}
                  {(ow?.fix_hints || []).length > 0 && (
                    <div className="text-[11px] mb-2 border-l-2 border-traffic-red pl-2 py-1 space-y-1" data-testid="ow-fix-hints">
                      <div className="uppercase tracking-widest font-bold text-traffic-red">Why it is not syncing</div>
                      {ow.fix_hints.map((h, i) => <p key={i} className="text-muted-foreground leading-snug">{h}</p>)}
                    </div>
                  )}
                  {(() => {
                    const order = ["whoop", "polar", "ultrahuman"];
                    const web = (ow?.sources || []).filter((s) => order.includes(s.id)).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
                    const anyConnected = web.some((s) => s.connected);
                    const visible = anyConnected ? web.filter((s) => s.connected) : web;
                    return (
                      <>
                        <div className="flex flex-wrap gap-2" data-testid="ow-providers">
                          {!anyConnected && (
                            <div className="flex items-center gap-2 border border-border px-3 py-1.5 text-sm bg-surface" data-testid="ow-src-trifit">
                              <span className="w-2 h-2 rounded-full" style={{ background: "#D2378B" }} />
                              Trifit
                              <span className="ml-1 text-[10px] uppercase tracking-widest text-muted-foreground">via Trifit app</span>
                            </div>
                          )}
                          {visible.map((p) => (
                            <div key={p.id} className="flex items-center gap-2 border border-border px-3 py-1.5 text-sm" data-testid={`ow-src-${p.id}`}
                              title={p.hint || p.error || ""}>
                              <span className={`w-2 h-2 rounded-full ${p.connected ? "bg-traffic-green" : p.needs_reconnect ? "bg-traffic-amber" : "bg-muted-foreground"}`} />
                              {p.name}
                              {p.needs_reconnect && <span className="text-[10px] uppercase tracking-widest text-traffic-amber" data-testid={`ow-status-${p.id}`}>{p.status}</span>}
                              {p.connected ? (
                                <button data-testid={`ow-disconnect-${p.id}`} onClick={() => disconnectOw(p.id)} className="ml-1 text-xs text-muted-foreground hover:text-traffic-red">Disconnect</button>
                              ) : (
                                <button data-testid={`ow-connect-${p.id}`} onClick={() => connectOw(p.id)} disabled={connecting === p.id || !ow?.configured} className="ml-1 flex items-center gap-1 text-xs hover:text-foreground text-muted-foreground disabled:opacity-40 disabled:cursor-not-allowed"><Plug className="w-3 h-3" /> {connecting === p.id ? "Opening…" : (p.needs_reconnect ? "Reconnect" : "Connect")}</button>
                              )}
                            </div>
                          ))}
                        </div>
                        {anyConnected
                          ? <p className="text-[11px] text-traffic-green mt-2" data-testid="ow-connected-note">Connected · data syncs automatically.</p>
                          : <p className="text-[11px] text-muted-foreground mt-2" data-testid="ow-app-note">Apple Health &amp; Google Health connect via the Trifit app.</p>}
                      </>
                    );
                  })()}
                </div>
                <div className="border border-border overflow-x-auto" data-testid="wearables-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
                        <th className="text-left p-3">Metric</th>
                        <th className="text-right p-3">Today</th>
                        <th className="text-right p-3">L7</th>
                        <th className="text-right p-3">L30</th>
                        <th className="text-right p-3">L90</th>
                        <th className="text-right p-3">Base</th>
                        <th className="text-right p-3">Δ base</th>
                        <th className="text-right p-3" title="Model confidence for this stat (sample size, freshness, noise, source)">Conf</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Only show metrics the currently connected wearable is actually reporting ·
                        // filter out stale rows (no reading in the last 30 days) so switching devices
                        // doesn't leave old BP / steps / mind-score fossils in the table.
                        const today = d.today ? new Date(d.today) : new Date();
                        const cutoff = today.getTime() - 30 * 24 * 3600 * 1000;
                        const visible = WEARABLE_ROWS.filter(([k]) => {
                          const m = d.wearables[k];
                          if (!m || m.latest == null) return false;
                          if (!m.as_of) return false;
                          const t = new Date(m.as_of).getTime();
                          return isFinite(t) && t >= cutoff;
                        });
                        if (visible.length === 0) {
                          return (
                            <tr><td colSpan={8} className="p-6 text-center text-xs text-muted-foreground" data-testid="wearables-empty">
                              No wearable readings in the last 30 days. Connect a device on the card below to start filling this in.
                            </td></tr>
                          );
                        }
                        return visible.map(([k, label]) => {
                          const m = d.wearables[k];
                          const lower = LOWER_BETTER_WEAR.has(k);
                          const up = m.latest != null && m.baseline != null && m.latest > m.baseline;
                          const flat = m.latest == null || m.baseline == null || m.latest === m.baseline;
                          const good = flat ? null : (lower ? !up : up);
                          const dpct = (m.latest != null && m.baseline) ? Math.round((m.latest - m.baseline) / m.baseline * 100) : null;
                          // "Today" is treated as any reading within the last 48h · WHOOP records recovery
                          // overnight so yesterday's date can still be the most current data available.
                          const ageDays = m.as_of ? (new Date(d.today || Date.now()).getTime() - new Date(m.as_of).getTime()) / 86400000 : 999;
                          const isCurrent = m.as_of && ageDays <= 2;
                          const conf = d.intelligence?.metrics?.[k]?.confidence;
                          return (
                            <tr key={k} className="border-b border-border last:border-0" data-testid={`wearable-row-${k}`}>
                              <td className="p-3 font-medium">{label}</td>
                              <td className="p-3 text-right tabular-nums font-heading font-bold" data-testid={`wearable-latest-${k}`}>
                                {isCurrent ? (m.latest ?? "-") : (
                                  <span title={`No reading in the last 48h · latest ${m.as_of || "-"}`}>
                                    <span className="text-muted-foreground">N/A</span>
                                    {m.latest != null && <span className="block text-[10px] font-normal text-muted-foreground">last {m.latest} · {m.as_of}</span>}
                                  </span>
                                )}
                              </td>
                              <td className="p-3 text-right tabular-nums text-muted-foreground">{m.last_7 ?? "-"}</td>
                              <td className="p-3 text-right tabular-nums text-muted-foreground">{m.l30 ?? "-"}</td>
                              <td className="p-3 text-right tabular-nums text-muted-foreground">{m.l90 ?? "-"}</td>
                              <td className="p-3 text-right tabular-nums text-muted-foreground">{m.baseline ?? "-"}</td>
                              <td className="p-3 text-right">
                                {dpct === null || flat ? <span className="text-muted-foreground">-</span> :
                                  <span className={`text-xs px-1.5 py-0.5 border tabular-nums ${good ? "border-traffic-green text-traffic-green" : "border-traffic-red text-traffic-red"}`}>{dpct > 0 ? "+" : ""}{dpct}%</span>}
                              </td>
                              <td className="p-3 text-right" data-testid={`wearable-conf-${k}`}>
                                {conf == null ? <span className="text-muted-foreground">-</span> :
                                  <span className={`text-xs px-1.5 py-0.5 border tabular-nums ${conf >= 75 ? "border-traffic-green text-traffic-green" : conf >= 50 ? "border-traffic-amber text-traffic-amber" : "border-traffic-red text-traffic-red"}`}>{conf}%</span>}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex-1">Notes</span>
                    <button data-testid="add-note-wearables" onClick={() => setWearNoteOpen((o) => !o)} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">+ Note</button>
                  </div>
                  {wearNoteOpen && (
                    <div className="flex gap-2 mb-3">
                      <input data-testid="note-input-wearables" value={wearNote} onChange={(e) => setWearNote(e.target.value)} placeholder="Note for wearables…" className="inp text-sm flex-1" />
                      <button data-testid="note-save-wearables" onClick={async () => { if (wearNote.trim()) { await addNote("wearables", wearNote); setWearNote(""); setWearNoteOpen(false); } }} className="text-sm px-3 bg-foreground text-background hover:bg-neutral-800 transition-colors">Save</button>
                    </div>
                  )}
                  {(notes.wearables || []).length === 0 ? <p className="text-xs text-muted-foreground">No notes yet.</p> : (
                    <div className="border border-border divide-y divide-border bg-surface" data-testid="notes-wearables">
                      {(notes.wearables || []).map((n) => <div key={n.note_id} className="p-2.5 text-sm">{n.text} <span className="text-xs text-muted-foreground">- {n.by}</span></div>)}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>

            {/* All roles see domain data now · medical stays restricted server-side */}
            <div className="space-y-6" data-testid="perf-zones">
              {ZONES.filter(([sec]) => can(SEC_OF[sec] || "performance")).map(([sec, lbl, tag, pres]) => (sec === "medical" && d?.clinical_restricted) ? null : (
                <div key={sec} className="border border-border" data-testid={`zone-wrap-${sec}`}>
                  <div id={`sec-${sec}`} className="px-5 py-3 border-b border-border flex items-center gap-2 scroll-mt-24">
                    <span className="text-xs tracking-[0.2em] uppercase font-bold flex-1">{lbl}</span>
                    <ScoreChip zone={sec} snap={snap} ir={ir} wear={d.wearables} />
                  </div>
                  {sec === "medical" && !d?.clinical_restricted && (d.injury_notes || []).length > 0 && (
                    <div className="border-b border-border divide-y divide-border" data-testid="medical-injury-records">
                      {d.injury_notes.map((n, i) => (
                        <div key={i} className="p-3 flex items-start gap-4">
                          <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 border ${n.severity === "severe" ? "border-traffic-red text-traffic-red" : n.severity === "moderate" ? "border-foreground" : "border-border text-muted-foreground"}`}>{n.severity}</span>
                          <div><div className="text-sm">{n.note}</div><div className="text-xs text-muted-foreground">{n.date}</div></div>
                        </div>
                      ))}
                    </div>
                  )}
                  <SectionStats athleteId={athleteId} section={sec} label={lbl} tag={tag} presets={pres}
                    silo={sec} notes={notes} onAddNote={addNote} notesOnly={sec === "medical" && user?.role === "athlete"} alwaysShow />
                </div>
              ))}
              {athleteId && (
                <div id="sec-bodycomp" className="scroll-mt-24">
                  <BodyComposition athleteId={athleteId} />
                </div>
              )}
            </div>
          </div>


          <Section icon={ListChecks} title="Full Timeline" testid="perf-timeline" anchor="sec-timeline">
            <div className="border border-border divide-y divide-border max-h-[420px] overflow-y-auto">
              {fTasks.length === 0 ? <div className="p-3 text-sm text-muted-foreground">No activities in this range.</div> :
                (tlAll ? [...fTasks].reverse() : [...fTasks].reverse().slice(0, 5)).map((t) => (
                <div key={t.task_id} className="p-3 flex items-center gap-4 text-sm" style={{ borderLeft: `3px solid ${DAY_TYPE_COLORS[t.day_type] || "#ccc"}` }}>
                  <span className="text-xs text-muted-foreground w-24 tabular-nums">{fmtDate(t.date)}</span>
                  <span className="text-xs uppercase tracking-widest font-bold w-20">{TASK_CLASS_LABELS[t.task_class] || t.task_class}</span>
                  <span className="flex-1">{t.title}</span>
                  {t.metrics && Object.keys(t.metrics).length > 0 && <span className="text-xs text-muted-foreground">{Object.entries(t.metrics).slice(0, 3).map(([k, v]) => `${k}:${v}`).join(" · ")}</span>}
                </div>
              ))}
            </div>
            {fTasks.length > 5 && (
              <button data-testid="timeline-expand" onClick={() => setTlAll((v) => !v)} className="mt-2 text-xs border border-border px-3 py-1.5 hover:bg-surface transition-colors">
                {tlAll ? "Show less" : `Show all ${fTasks.length}`}
              </button>
            )}
          </Section>

          {athleteId && <div id="sec-reports" className="scroll-mt-24"><ReportsPanel athleteId={athleteId} /></div>}
        </>
      )}
    </div>
  );
}
